import { Bell, Check } from "lucide-react";
import type { ReactNode } from "react";

import { Section, SectionHead, card, figure } from "../../components/section";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Times for the drawing in step one. They illustrate a calendar; they are not anyone's open times. */
const SAMPLE_TIMES = ["07:00", "08:00", "12:00", "17:00", "18:00", "19:00"];
const SAMPLE_PICK = 4;

const mini = "w-[min(260px,100%)] rounded-[20px] bg-[var(--site-well)] p-3.5";
const badge = "grid size-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground";

const MiniRow = ({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) => (
  <div className={cn(mini, "flex items-center gap-2.5 font-medium")}>
    <span className={badge}>{icon}</span>
    <div>
      {title}
      <small className="block text-xs font-normal text-muted-foreground">{detail}</small>
    </div>
  </div>
);

const steps = () => [
  {
    title: m.landing_how_pick_title(),
    body: m.landing_how_pick_body(),
    art: (
      <div className={cn(mini, "grid grid-cols-3 gap-1.5")}>
        {SAMPLE_TIMES.map((time, index) => (
          <span
            key={time}
            className={cn(
              "grid h-9 place-items-center rounded-[10px] bg-card text-xs font-medium tabular-nums",
              index === SAMPLE_PICK && "bg-primary font-semibold text-primary-foreground",
            )}
          >
            {time}
          </span>
        ))}
      </div>
    ),
  },
  {
    title: m.landing_how_yes_title(),
    body: m.landing_how_yes_body(),
    art: (
      <MiniRow
        icon={<Check className="size-4" />}
        title={m.landing_how_yes_card_title()}
        detail={m.landing_how_yes_card_detail()}
      />
    ),
  },
  {
    title: m.landing_how_show_title(),
    body: m.landing_how_show_body(),
    art: (
      <MiniRow
        icon={<Bell className="size-4" />}
        title={m.landing_how_show_card_title()}
        detail={m.landing_how_show_card_detail()}
      />
    ),
  },
];

/** Booking in three steps, each with a small drawing of the screen it happens on. */
export const HowItWorks = () => (
  <Section>
    <SectionHead
      title={
        <>
          {m.landing_how_title_lead()}
          <br />
          {m.landing_how_title_tail()}
        </>
      }
    />
    <ol className="grid gap-3 lg:grid-cols-3">
      {steps().map((step, index) => (
        <li key={step.title} className={cn(card, "flex min-h-[280px] flex-col lg:min-h-[340px]")}>
          <span className={cn(figure, "text-[40px] text-[var(--site-dot)]")}>{String(index + 1).padStart(2, "0")}</span>
          <div aria-hidden className="grid flex-1 place-items-center py-[18px]">
            {step.art}
          </div>
          <b className="text-3xl font-medium tracking-[-0.03em]">{step.title}</b>
          <p className="text-muted-foreground">{step.body}</p>
        </li>
      ))}
    </ol>
  </Section>
);
